import { announce, debounce, fetchSection } from './theme.js';

class FacetFilters extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.addEventListener('change', this.onChange);
    this.addEventListener('input', this.onInput);
    this.addEventListener('submit', (e) => {
      const form = e.target.closest('form');
      if (!form) return;
      e.preventDefault();
      if (!form.matches('[data-facets-form]')) {
        this.apply(this.paramsFrom(this.querySelector('[data-facets-form]') || form));
        return;
      }

      const drawer = form.closest('dialog');
      this.apply(this.paramsFrom(form)).finally(() => drawer?.close());
    });
    this.addEventListener('click', this.onClick);
    document.addEventListener('click', this.onOutsideClick);
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('popstate', this.onPop);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPop);
    document.removeEventListener('click', this.onOutsideClick);
    document.removeEventListener('keydown', this.onKeydown);
  }

  onPop = () => this.render(window.location.search.slice(1), false);

  onOutsideClick = (e) => {
    this.querySelectorAll('.facet--dropdown[data-open]').forEach((facet) => {
      if (!facet.contains(e.target)) this.setDropdown(facet, false);
    });
  };

  onKeydown = (e) => {
    if (e.key !== 'Escape') return;
    const facet = this.querySelector('.facet--dropdown[data-open]');
    if (!facet) return;
    this.setDropdown(facet, false);
    facet.querySelector('[data-facet-toggle]')?.focus();
  };

  setDropdown(facet, open) {
    const toggle = facet.querySelector('[data-facet-toggle]');
    const panel = facet.querySelector('.facet__panel');
    if (!toggle || !panel) return;
    facet.toggleAttribute('data-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    panel.hidden = !open;
    facet.removeAttribute('data-align');
    if (!open) return;
    requestAnimationFrame(() => {
      if (!facet.isConnected || !facet.hasAttribute('data-open')) return;
      if (panel.getBoundingClientRect().right > document.documentElement.clientWidth - 8) {
        facet.setAttribute('data-align', 'end');
      }
    });
  }

  paramsFrom(form) {
    const params = new URLSearchParams();
    for (const [k, v] of new FormData(form)) {
      if (v !== '' && v != null) params.append(k, v);
    }
    const sort = this.querySelector('[data-sort]')?.value || new URLSearchParams(window.location.search).get('sort_by');
    if (sort) params.set('sort_by', sort);
    const q = new URLSearchParams(window.location.search).get('q');
    if (q && !params.has('q')) params.set('q', q);
    const type = new URLSearchParams(window.location.search).get('type');
    if (type && !params.has('type')) params.set('type', type);
    return params.toString();
  }

  onChange = (e) => {
    if (e.target.matches('[data-sort]')) {
      const params = new URLSearchParams(window.location.search);
      params.set('sort_by', e.target.value);
      params.delete('page');
      this.render(params.toString());
      return;
    }
    const form = e.target.closest('[data-facets-form]');
    if (!form || e.target.matches('[data-price-input]')) return;
    if (form.hasAttribute('data-manual')) return;
    this.apply(this.paramsFrom(form));
  };

  onInput = debounce((e) => {
    if (!e.target.matches('[data-price-input]')) return;
    const form = e.target.closest('[data-facets-form]');
    if (form && !form.hasAttribute('data-manual')) this.apply(this.paramsFrom(form));
  }, 600);

  onClick = (e) => {
    const toggle = e.target.closest('[data-facet-toggle]');
    if (toggle) {
      const facet = toggle.closest('.facet--dropdown');
      const next = !facet.hasAttribute('data-open');
      this.querySelectorAll('.facet--dropdown[data-open]').forEach((other) => {
        if (other !== facet) this.setDropdown(other, false);
      });
      this.setDropdown(facet, next);
      return;
    }
    if (this.onLabelClick(e)) return;
    const link = e.target.closest('[data-facet-link]');
    if (!link) return;
    e.preventDefault();
    const scrollTop = link.hasAttribute('data-scroll-top');

    const params = new URL(link.href, window.location.origin).searchParams;
    const sort = this.querySelector('[data-sort]')?.value;
    if (sort && !params.has('sort_by')) params.set('sort_by', sort);
    this.render(params.toString()).then(() => {
      if (scrollTop) {
        this.scrollIntoView({ block: 'start' });
        this.querySelector('[data-grid] a')?.focus({ preventScroll: true });
      }
    });
  };

  onLabelClick(e) {
    if (e.target.closest('input, a, button')) return false;
    const label = e.target.closest('.facet__value');
    const input = label?.querySelector('input[type="checkbox"]');
    if (!input || input.disabled) return false;
    e.preventDefault();
    e.stopPropagation();
    input.focus({ preventScroll: true });
    input.click();
    return true;
  }

  apply(query) {
    return this.render(query);
  }

  fallback(url) {
    window.location.assign(url);
  }

  async render(query, push = true) {
    const url = `${window.location.pathname}${query ? `?${query}` : ''}`;

    this.abort?.abort();
    const controller = (this.abort = new AbortController());
    this.setAttribute('aria-busy', 'true');
    this.querySelector('[data-facets-region="grid"]')?.setAttribute('aria-busy', 'true');
    const openIds = Array.from(this.querySelectorAll('details[open], .facet--dropdown[data-open]'))
      .map((d) => d.id)
      .filter(Boolean);
    const focusId = document.activeElement?.id;
    try {
      let doc;
      try {
        doc = await fetchSection(url, this.sectionId, { signal: controller.signal });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('[facets] section request failed, falling back to a full page load:', url, err);
          this.fallback(url);
        }
        return;
      }
      if (controller !== this.abort) return;
      doc.querySelectorAll('[data-facets-region]').forEach((fresh) => {
        const key = fresh.dataset.facetsRegion;
        this.querySelectorAll(`[data-facets-region="${key}"]`).forEach((current) => {
          current.replaceWith(document.importNode(fresh, true));
        });
      });

      this.querySelectorAll('details[id]').forEach((d) => {
        if (openIds.includes(d.id)) d.setAttribute('open', '');
      });
      this.querySelectorAll('.facet--dropdown[id]').forEach((d) => {
        if (openIds.includes(d.id)) this.setDropdown(d, true);
      });
      if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
      if (push) window.history.pushState({}, '', url);
      const count = this.querySelector('[data-results-count]')?.textContent?.trim();
      if (count) announce(count);
    } finally {
      if (controller === this.abort) {
        this.removeAttribute('aria-busy');
        this.querySelector('[data-facets-region="grid"]')?.removeAttribute('aria-busy');
      }
    }
  }
}
customElements.define('facet-filters', FacetFilters);

class LoadMore extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', () => this.load());
    if (this.hasAttribute('infinite')) {
      this.io = new IntersectionObserver(([entry]) => entry.isIntersecting && this.load(), { rootMargin: '600px 0px' });
      this.io.observe(this);
    }
  }

  disconnectedCallback() {
    this.io?.disconnect();
  }

  async load() {
    if (this.loading || !this.dataset.nextUrl) return;
    this.loading = true;
    this.button?.setAttribute('aria-busy', 'true');
    const section = this.closest('[data-section-id]');
    try {
      let doc;
      try {
        doc = await fetchSection(this.dataset.nextUrl, section.dataset.sectionId);
      } catch {

        window.location.assign(this.dataset.nextUrl);
        return;
      }
      const grid = section.querySelector('[data-grid]');
      const incoming = doc.querySelector('[data-grid]');
      const firstNew = incoming?.firstElementChild;
      incoming && grid.append(...Array.from(incoming.children).map((n) => document.importNode(n, true)));
      const nextMore = doc.querySelector('load-more');
      if (nextMore) {
        this.dataset.nextUrl = nextMore.dataset.nextUrl || '';
        const progress = nextMore.querySelector('[data-progress]');
        if (progress) this.querySelector('[data-progress]')?.replaceWith(document.importNode(progress, true));
      }
      if (!nextMore || !this.dataset.nextUrl) this.remove();

      if (firstNew && !this.hasAttribute('infinite')) {
        const link = grid.querySelector(`[data-product-id="${firstNew.dataset.productId}"] a`);
        link?.focus({ preventScroll: false });
      }
    } finally {
      this.loading = false;
      this.button?.removeAttribute('aria-busy');
    }
  }
}
customElements.define('load-more', LoadMore);

class PriceRange extends HTMLElement {
  connectedCallback() {
    this.ranges = Array.from(this.querySelectorAll('input[type="range"]'));
    this.numbers = Array.from(this.closest('.facet__price')?.querySelectorAll('input[type="number"]') || []);
    if (this.ranges.length !== 2 || this.numbers.length !== 2) return;
    this.max = Number(this.ranges[0].max) || 1;
    this.ranges.forEach((range, i) => range.addEventListener('input', () => this.fromRange(i)));
    this.numbers.forEach((input, i) => input.addEventListener('input', () => this.fromNumber(i)));
    this.paint();
  }

  fromRange(i) {
    let [low, high] = this.ranges.map((r) => Number(r.value));
    if (low > high) {
      if (i === 0) low = high;
      else high = low;
      this.ranges[i].value = i === 0 ? low : high;
    }

    this.numbers[0].value = low > 0 ? low : '';
    this.numbers[1].value = high < this.max ? high : '';
    this.paint();
  }

  fromNumber(i) {
    const raw = this.numbers[i].value;
    const value = raw === '' ? (i === 0 ? 0 : this.max) : Math.min(Math.max(Number(raw), 0), this.max);
    this.ranges[i].value = value;
    this.paint();
  }

  paint() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.render();
    });
  }

  disconnectedCallback() {
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  render() {
    const [low, high] = this.ranges.map((r) => Number(r.value));
    this.style.setProperty('--from', `${(Math.min(low, high) / this.max) * 100}%`);
    this.style.setProperty('--to', `${(Math.max(low, high) / this.max) * 100}%`);

    const currency = this.dataset.currency || '';
    const labels = Array.from(this.querySelectorAll('[data-price-value]'));
    labels.forEach((label, i) => {
      const value = i === 0 ? low : high;
      label.textContent = `${currency}${value}`;
      label.style.setProperty('--pos', value / this.max);
      label.style.translate = '';
      this.ranges[i]?.setAttribute('aria-valuetext', `${currency}${value}`);
    });

    if (labels.length === 2) {
      const [a, b] = labels.map((l) => l.getBoundingClientRect());
      const overlap = a.right + 6 - b.left;
      if (a.width && overlap > 0) {
        labels[0].style.translate = `calc(-50% - ${overlap / 2}px) 0`;
        labels[1].style.translate = `calc(-50% + ${overlap / 2}px) 0`;
      }
    }

    this.ranges[0].style.zIndex = low >= this.max ? 3 : '';
  }
}
customElements.define('price-range', PriceRange);
